import { useMemo } from "react";
import { BeratungSeite } from "./useBeratungSeiten";

/**
 * Filters consultation pages based on selected priorities.
 * Pages with empty priority_mapping are always visible.
 * Pages with priority_mapping are only visible if at least one priority is selected.
 */
export function useFilteredSeiten(
  allSeiten: BeratungSeite[],
  formValues: Record<string, any>
): BeratungSeite[] {
  return useMemo(() => {
    // Extract selected priorities from form values
    // The selected_priorities field stores the selected priority IDs (from tile clicks)
    const selectedPriorities: string[] = formValues['selected_priorities'] || formValues['priorities'] || [];
    
    // If no priorities selected, show all pages (including those with mappings)
    // This allows initial navigation before priorities are set
    if (selectedPriorities.length === 0) {
      return allSeiten;
    }
    
    return allSeiten.filter(seite => {
      // Pages without priority_mapping are always visible (core pages like Prioritäten, Datenerfassung, etc.)
      if (!seite.priority_mapping || seite.priority_mapping.length === 0) {
        return true;
      }
      
      // Page is visible if at least one of its mapped priorities is selected
      return seite.priority_mapping.some(priority => 
        selectedPriorities.includes(priority)
      );
    });
  }, [allSeiten, formValues]);
}

/**
 * Checks if a specific page should be visible based on selected priorities.
 */
export function isPageVisible(
  seite: BeratungSeite,
  selectedPriorities: string[]
): boolean {
  // Pages without priority_mapping are always visible
  if (!seite.priority_mapping || seite.priority_mapping.length === 0) {
    return true;
  }
  
  // If no priorities selected, show all pages
  if (selectedPriorities.length === 0) {
    return true;
  }
  
  // Page is visible if at least one of its mapped priorities is selected
  return seite.priority_mapping.some(priority => 
    selectedPriorities.includes(priority)
  );
}
